import kn_logo_white from '_assets/img/kan-logo-white.png';
import Button from '_atoms/Button/Button';
import React, { useEffect, useState } from 'react';
import './onboarding.scss';
import './ReferralIntroScreen.scss';

import happyKnitters from '_assets/img/onboard/intro1.avif';
import i18n from '_i18n';
import ReferralApi, {
    type ReferralLinkResponse,
} from '_services/Api/Referral/ReferralApi';
import ReferralContextService from '_services/Referral/ReferralContextService';
import { termsAndConditionsUrl } from 'Helper/Terms';
import useNavigate from 'Hooks/useNavigate';

const texts = {
    no: {
        from: 'har gitt deg',
        heading: '30 dager gratis prøveperiode',
        conditions: 'Ved å fortsette godtar jeg <u>Betingelser og vilkår</u>',
        cta: 'Opprett bruker og start prøveperiode',
    },
    en: {
        from: 'has given you',
        heading: 'a 30-day free trial',
        conditions: 'By continuing I agree with the <u>Terms & Conditions</u>',
        cta: 'Create user and start the free trial',
    },
};

const referralMetaInFlight = new Map<string, Promise<ReferralLinkResponse>>();
const referralMetaCache = new Map<string, ReferralLinkResponse>();

const getReferralMeta = async (
    referral_uuid: string,
): Promise<ReferralLinkResponse> => {
    const cached = referralMetaCache.get(referral_uuid);
    if (cached) {
        return cached;
    }

    const pending = referralMetaInFlight.get(referral_uuid);
    if (pending) {
        return pending;
    }

    const nextRequest = ReferralApi.show(referral_uuid)
        .then((response) => {
            referralMetaCache.set(referral_uuid, response.data);
            return response.data;
        })
        .finally(() => {
            referralMetaInFlight.delete(referral_uuid);
        });

    referralMetaInFlight.set(referral_uuid, nextRequest);
    return nextRequest;
};

/** First screen shown to new users that get a referral link from a friend */
const ReferralIntroScreen: React.FC = () => {
    /** disable scrolling on body, as set by app.tsx. Reset by BaseLayout.tsx    */
    useEffect(() => {
        document.body.classList.add('full-screen');
    }, []);

    const navigate = useNavigate();
    const language: LanguageCode = i18n.language === 'no' ? 'no' : 'en';
    const { from, heading, conditions, cta } = texts[language];

    const referral_uuid = ReferralContextService.getUuid();
    const [referred_by_name, setReferredByName] = useState<string | null>(null);

    useEffect(() => {
        if (!referral_uuid) {
            navigate('/landing');
            return;
        }

        let mounted = true;

        getReferralMeta(referral_uuid)
            .then((referralMeta) => {
                if (!mounted) {
                    return;
                }
                setReferredByName(referralMeta.sender_first_name);
            })
            .catch(() => undefined);

        return () => {
            mounted = false;
        };
    }, [navigate, referral_uuid]);

    return (
        <div
            className="onboarding-page-wrapper "
            style={{
                backgroundImage: `url(${happyKnitters})`,
            }}
        >
            <div className="onboarding-content-kn-logo">
                <img src={kn_logo_white} alt="Knit & Note" />
            </div>

            <div className="onboarding-gradient-section">
                <div className="onboarding-lower-gradient"></div>
                <div className="onboarding-bottom-section"></div>
            </div>

            <div className="intro-text-wrapper referral-text-wrapper">
                {referred_by_name && (
                    <div className="referral-from">
                        {referred_by_name} {from}
                    </div>
                )}
                <div
                    className="referral-heading"
                    dangerouslySetInnerHTML={{ __html: heading }}
                ></div>
                <div
                    onClick={() =>
                        window.open(
                            termsAndConditionsUrl,
                            '_blank',
                            'noopener,noreferrer',
                        )
                    }
                    className="referral-conditions"
                    dangerouslySetInnerHTML={{ __html: conditions }}
                ></div>
            </div>
            <div className="button-wrapper">
                <Button onClick={() => navigate('/register/country')}>
                    {cta}
                </Button>
            </div>
        </div>
    );
};

export default ReferralIntroScreen;
